/**
 * Enrichment sideload config — entity-agnostic.
 *
 * "Sideloaded" enrichments are slow at bulk scale, so the selector fetch runs
 * them in a progressive second pass (per-cell spinners fill in) instead of the
 * main enrich request. Whether a slug is sideloaded is a property of the
 * enrichment, not of any one table or entity type — the backend catalog decides
 * which types each slug actually applies to (abuseipdb is IP-only; observations
 * runs for every selector type).
 *
 * Shared by useSelectorFetch (the generic fetch) and the IP table registry.
 */

/**
 * Each entry:
 *   - batchSize:  max selectors per request for this enrichment
 *   - chunkSizes: initial small batches so the first cells resolve quickly
 *   - empty:      placeholder blob written to each row while loading, so the
 *                 column renders (and the slug is "present") before data lands
 * Add a slow enrichment here to sideload it — no other wiring required.
 * @type {Array<{slug: string, batchSize: number, chunkSizes: number[], empty: Object}>}
 */
export const SIDELOAD_ENRICHMENTS = [
  { slug: 'webscout_observations', batchSize: 40, chunkSizes: [3, 5, 10], empty: { total_count: 0, breakdown: [] } },
  { slug: 'abuseipdb',             batchSize: 20, chunkSizes: [3, 5, 10], empty: {} },
]

// The slugs sideloaded in the progressive pass (derived from SIDELOAD_ENRICHMENTS).
export const SIDELOAD_SLUGS = SIDELOAD_ENRICHMENTS.map(e => e.slug)

/**
 * Look up the sideload config for a slug, or undefined if it isn't sideloaded.
 * @param {string} slug
 */
export function sideloadConfig(slug) {
  return SIDELOAD_ENRICHMENTS.find(e => e.slug === slug)
}

/**
 * Progressive batching: a few small chunks first (so the first cells resolve
 * quickly), then the rest at the enrichment's batch size.
 * @param {Array} arr
 * @param {number[]} chunkSizes
 * @param {number} batchSize
 * @returns {Array<Array>}
 */
export function progressiveChunks(arr, chunkSizes, batchSize) {
  const out = []
  let i = 0
  for (const size of chunkSizes) {
    if (i >= arr.length) break
    const take = Math.min(size, batchSize)
    out.push(arr.slice(i, i + take))
    i += take
  }
  while (i < arr.length) {
    out.push(arr.slice(i, i + batchSize))
    i += batchSize
  }
  return out
}

/**
 * Humanize a slug for display: 'webscout_threat_score' → 'Threat Score'.
 * @param {string} slug
 * @returns {string}
 */
export function slugLabel(slug) {
  if (!slug) return ''
  return slug
    .replace(/^webscout_/, '')
    .split('_')
    .filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ')
}
